/**
 * Utilidades para trabajar con tokens de tema configurables.
 * Todas las funciones usan tokens en lugar de valores hardcodeados.
 */

/**
 * Obtiene el color de un rol desde los tokens configurables
 * @param {string} role - Nombre del rol (ej: 'SUPERUSER', 'OWNER', etc.)
 * @param {object} tokens - Tokens del tema (debe incluir roleColors)
 * @returns {string} Color del rol o color por defecto
 */
export const getRoleColor = (role, tokens) => {
  if (!tokens || !tokens.roleColors) {
    return "default";
  }
  return tokens.roleColors[role] || tokens.roleColors[role] || "default";
};

/**
 * Genera un gradiente usando los colores configurables del tema
 * @param {object} tokens - Tokens del tema (debe incluir gradientColors)
 * @param {string} direction - Dirección del gradiente (default: '135deg')
 * @returns {string} CSS gradient string
 */
export const getGradient = (tokens, direction = "135deg") => {
  // Si no hay gradientColors, usar primaryColor como base y generar un gradiente
  const primaryColor = tokens?.colorPrimary || "#000000";
  
  // Si el primary color es negro, usar gradiente de grises oscuros elegante (prioridad sobre gradientColors)
  if (primaryColor === "#000000" || primaryColor.toLowerCase() === "#000" || primaryColor === "rgb(0, 0, 0)") {
    return `linear-gradient(${direction}, #2d2d2d 0%, #1a1a1a 50%, #000000 100%)`;
  }
  
  // Si hay gradientColors configurados Y el primary color NO es negro, usarlos
  if (tokens?.gradientColors?.start && tokens?.gradientColors?.end) {
    const { start, end } = tokens.gradientColors;
    return `linear-gradient(${direction}, ${start} 0%, ${end} 100%)`;
  }
  
  // Generar un color más oscuro para el gradiente
  const hex = primaryColor.replace("#", "");
  
  // Validar que el hex sea válido (6 caracteres)
  if (hex.length !== 6) {
    // Fallback a gradiente de grises si el color no es válido
    return `linear-gradient(${direction}, #2d2d2d 0%, #1a1a1a 50%, #000000 100%)`;
  }
  
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Validar que los valores RGB sean válidos
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return `linear-gradient(${direction}, #2d2d2d 0%, #1a1a1a 50%, #000000 100%)`;
  }
  
  // Crear una variación más oscura (reducir brillo en 30%)
  const darkerR = Math.max(0, Math.floor(r * 0.7));
  const darkerG = Math.max(0, Math.floor(g * 0.7));
  const darkerB = Math.max(0, Math.floor(b * 0.7));
  
  // Crear una variación intermedia (reducir brillo en 15%)
  const midR = Math.max(0, Math.floor(r * 0.85));
  const midG = Math.max(0, Math.floor(g * 0.85));
  const midB = Math.max(0, Math.floor(b * 0.85));
  
  const darkerColor = `rgb(${darkerR}, ${darkerG}, ${darkerB})`;
  const midColor = `rgb(${midR}, ${midG}, ${midB})`;
  
  // Gradiente de 3 colores para mejor efecto visual
  return `linear-gradient(${direction}, ${primaryColor} 0%, ${midColor} 50%, ${darkerColor} 100%)`;
};

/**
 * Obtiene un color semántico desde los tokens configurables
 * @param {string} type - Tipo de color semántico ('success', 'warning', 'error', 'info')
 * @param {object} tokens - Tokens del tema (debe incluir semanticColors)
 * @returns {string} Color semántico o color por defecto
 */
export const getSemanticColor = (type, tokens) => {
  if (!tokens || !tokens.semanticColors) {
    const defaults = {
      success: "#52c41a",
      warning: "#faad14",
      error: "#ff4d4f",
      info: "#1890ff",
    };
    return defaults[type] || defaults.info;
  }
  return tokens.semanticColors[type] || tokens.semanticColors.info || "#1890ff";
};

/**
 * Obtiene un color de estado desde los tokens configurables
 * @param {string} status - Estado ('critical', 'low', 'normal', 'high', 'outOfStock', 'inStock')
 * @param {object} tokens - Tokens del tema (debe incluir statusColors)
 * @returns {string} Color de estado o color por defecto
 */
export const getStatusColor = (status, tokens) => {
  if (!tokens || !tokens.statusColors) {
    const defaults = {
      critical: "#ff4d4f",
      low: "#faad14",
      normal: "#52c41a",
      high: "#13c2c2",
      outOfStock: "#ff7875",
      inStock: "#52c41a",
    };
    return defaults[status] || defaults.normal;
  }
  return tokens.statusColors[status] || tokens.statusColors.normal || "#52c41a";
};

/**
 * Genera variaciones de un color (más oscuro o más claro)
 * @param {string} color - Color en formato hex
 * @param {number} amount - Cantidad a ajustar (-100 a 100, negativo = más oscuro, positivo = más claro)
 * @returns {string} Color ajustado en formato rgb
 */
export const getColorVariation = (color, amount = -20) => {
  // Convertir hex a rgb
  const hex = color.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Ajustar brillo
  const newR = Math.max(0, Math.min(255, r + amount));
  const newG = Math.max(0, Math.min(255, g + amount));
  const newB = Math.max(0, Math.min(255, b + amount));

  return `rgb(${newR}, ${newG}, ${newB})`;
};

/**
 * Genera una sombra basada en la elevación usando tokens
 * @param {number} elevation - Nivel de elevación (1-5)
 * @param {object} tokens - Tokens del tema
 * @param {boolean} isDark - Si está en modo oscuro
 * @returns {string} CSS box-shadow
 */
export const getShadow = (elevation = 1, tokens, isDark = false) => {
  const shadows = {
    1: isDark
      ? "0 2px 8px rgba(0, 0, 0, 0.45)"
      : "0 2px 8px rgba(0, 0, 0, 0.15)",
    2: isDark
      ? "0 4px 16px rgba(0, 0, 0, 0.5)"
      : "0 4px 16px rgba(0, 0, 0, 0.12)",
    3: isDark
      ? "0 8px 24px rgba(0, 0, 0, 0.55)"
      : "0 8px 24px rgba(0, 0, 0, 0.15)",
    4: isDark
      ? "0 12px 32px rgba(0, 0, 0, 0.6)"
      : "0 12px 32px rgba(0, 0, 0, 0.18)",
    5: isDark
      ? "0 16px 48px rgba(0, 0, 0, 0.65)"
      : "0 16px 48px rgba(0, 0, 0, 0.2)",
  };

  return shadows[Math.min(5, Math.max(1, elevation))] || shadows[1];
};

/**
 * Calcula el color de texto apropiado (blanco o negro) basado en el contraste con el fondo
 * Usa la fórmula de luminancia relativa W3C para determinar el mejor contraste
 * @param {string} backgroundColor - Color de fondo en formato HEX (ej: #000000) o RGB (ej: rgb(0,0,0))
 * @param {string} lightText - Color de texto para fondos oscuros (default: #ffffff)
 * @param {string} darkText - Color de texto para fondos claros (default: #000000)
 * @returns {string} Color de texto apropiado
 */
export const getContrastTextColor = (
  backgroundColor,
  lightText = "#ffffff",
  darkText = "#000000"
) => {
  if (!backgroundColor) return darkText;

  // Extraer RGB del color de fondo
  let r, g, b;

  // Si es formato HEX
  if (backgroundColor.startsWith("#")) {
    const hex = backgroundColor.replace("#", "");
    if (hex.length === 3) {
      // Formato corto #RGB
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else {
      return darkText; // Formato inválido
    }
  }
  // Si es formato RGB
  else if (backgroundColor.startsWith("rgb")) {
    const matches = backgroundColor.match(/\d+/g);
    if (matches && matches.length >= 3) {
      r = parseInt(matches[0], 10);
      g = parseInt(matches[1], 10);
      b = parseInt(matches[2], 10);
    } else {
      return darkText; // Formato inválido
    }
  } else {
    return darkText; // Formato no soportado
  }

  // Validar valores RGB
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return darkText;
  }

  // Calcular luminancia relativa usando fórmula W3C
  // https://www.w3.org/WAI/GL/wiki/Relative_luminance
  const getLuminance = (val) => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  };

  const luminance =
    0.2126 * getLuminance(r) +
    0.7152 * getLuminance(g) +
    0.0722 * getLuminance(b);

  // Si la luminancia es menor a 0.5, el fondo es oscuro, usar texto claro
  // Si es mayor o igual a 0.5, el fondo es claro, usar texto oscuro
  return luminance < 0.5 ? lightText : darkText;
};

/**
 * Obtiene el color de texto apropiado para un gradiente
 * Analiza el color más claro del gradiente para determinar el contraste
 * @param {string} gradient - Gradiente CSS (ej: linear-gradient(...))
 * @param {object} tokens - Tokens del tema
 * @returns {string} Color de texto apropiado
 */
export const getGradientTextColor = (gradient, tokens) => {
  if (!gradient) return tokens?.colorText || "#000000";

  // Si el gradiente contiene colores oscuros conocidos, usar texto blanco
  const darkColors = [
    "#000000",
    "#1a1a1a",
    "#2d2d2d",
    "rgb(0, 0, 0)",
    "rgb(26, 26, 26)",
    "rgb(45, 45, 45)",
  ];

  // Extraer colores del gradiente
  const gradientLower = gradient.toLowerCase();
  const hasDarkColor = darkColors.some((color) =>
    gradientLower.includes(color.toLowerCase())
  );

  // Si el primary color es negro, el gradiente será oscuro
  const primaryColor = tokens?.colorPrimary || "#000000";
  if (primaryColor === "#000000" || primaryColor.toLowerCase() === "#000") {
    return "#ffffff";
  }

  // Si el gradiente contiene colores oscuros, usar texto blanco
  if (hasDarkColor) {
    return "#ffffff";
  }

  // Por defecto, usar el color de texto del tema
  return tokens?.colorText || "#000000";
};

